import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface Tenant {
  id: string;
  userId: string;
  companyName: string;
  phoneNumber: string;
  createdAt: string;
  updatedAt: string;
}

interface User {
  id: string;
  email: string;
  isAdmin: boolean;
  approved: boolean;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  tenant?: Tenant | null;
}

const TenantManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/user`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to fetch users');
      }

      setUsers(data.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId: string) => {
    setActionLoading(`approve-${userId}`);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/user/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ approved: true }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to approve user');
      }

      await fetchUsers();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to approve user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId: string) => {
    if (!window.confirm('Are you sure you want to reject this tenant request? This will delete the user.')) {
      return;
    }

    setActionLoading(`reject-${userId}`);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/user/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || 'Failed to reject user');
      }

      await fetchUsers();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to reject user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this tenant? This action cannot be undone.')) {
      return;
    }

    setActionLoading(`delete-${userId}`);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/user/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || 'Failed to delete user');
      }

      await fetchUsers();
      setShowDetailModal(false);
      setSelectedUser(null);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewDetails = (user: User) => {
    setSelectedUser(user);
    setShowDetailModal(true);
  };

  const pendingRequests = users.filter(user => !user.isAdmin && !user.approved);
  const approvedTenants = users.filter(user => !user.isAdmin && user.approved);

  if (loading) {
    return (
      <div className="w-full p-8">
        <div className="bg-gradient-to-br from-main-medium via-main-medium to-main-dark rounded-2xl p-8 shadow-xl backdrop-blur-sm">
          <div className="text-gray-400 text-center">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-8 space-y-8">
      {/* Statistics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-yellow-600/20 to-yellow-700/20 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-yellow-500/20">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span className="text-3xl font-bold text-yellow-400">{pendingRequests.length}</span>
          </div>
          <h3 className="text-white font-semibold mb-1">Pending Requests</h3>
          <p className="text-gray-400 text-sm">Awaiting approval</p>
        </div>

        <div className="bg-gradient-to-br from-green-600/20 to-green-700/20 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-green-500/20">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span className="text-3xl font-bold text-green-400">{approvedTenants.length}</span>
          </div>
          <h3 className="text-white font-semibold mb-1">Approved Tenants</h3>
          <p className="text-gray-400 text-sm">Active tenants</p>
        </div>

        <div className="bg-gradient-to-br from-blue-600/20 to-blue-700/20 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-blue-500/20">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <span className="text-3xl font-bold text-blue-400">{users.length - 1}</span>
          </div>
          <h3 className="text-white font-semibold mb-1">Total Users</h3>
          <p className="text-gray-400 text-sm">All registered users</p>
        </div>
      </div>

      {/* Tenant Requests Section */}
      <div className="bg-gradient-to-br from-main-medium via-main-medium to-main-dark rounded-2xl p-8 shadow-xl backdrop-blur-sm">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl shadow-lg">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-white tracking-tight">
              Tenant Requests
            </h2>
            {pendingRequests.length > 0 && (
              <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-semibold rounded-full border border-yellow-500/30">
                {pendingRequests.length} pending
              </span>
            )}
          </div>
        </div>
        {error && (
          <div className="p-4 bg-red-500/10 backdrop-blur-sm rounded-xl text-red-200 text-sm mb-6 shadow-lg border-l-4 border-red-500">
            {error}
          </div>
        )}
        {pendingRequests.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-main-light/30 rounded-full mb-4">
              <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-gray-400 text-lg font-medium">No pending tenant requests</p>
            <p className="text-gray-500 text-sm mt-2">All requests have been processed</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingRequests.map((user) => (
              <div
                key={user.id}
                className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.01] border border-white/5"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      {user.tenant && (
                        <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-yellow-600 to-yellow-700 rounded-lg shadow-md">
                          <span className="text-white font-bold text-sm">
                            {user.tenant.companyName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        {user.tenant && (
                          <h3 className="text-lg font-bold text-white">{user.tenant.companyName}</h3>
                        )}
                        <p className="text-gray-400 text-sm">{user.email}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ml-13">
                      {user.tenant && (
                        <div className="flex items-center gap-2 text-gray-300 text-sm">
                          <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {user.tenant.phoneNumber}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-300 text-sm">
                        <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {new Date(user.createdAt).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-semibold rounded-full border border-yellow-500/30">
                          Pending
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-6">
                    <button
                      onClick={() => handleApprove(user.id)}
                      disabled={actionLoading === `approve-${user.id}`}
                      className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed flex items-center gap-2 hover:scale-105 active:scale-95"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {actionLoading === `approve-${user.id}` ? 'Approving...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => handleReject(user.id)}
                      disabled={actionLoading === `reject-${user.id}`}
                      className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed flex items-center gap-2 hover:scale-105 active:scale-95"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      {actionLoading === `reject-${user.id}` ? 'Rejecting...' : 'Reject'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tenant List Section */}
      <div className="bg-gradient-to-br from-main-medium via-main-medium to-main-dark rounded-2xl p-8 shadow-xl backdrop-blur-sm">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-white tracking-tight">
              Approved Tenants
            </h2>
            {approvedTenants.length > 0 && (
              <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-semibold rounded-full border border-green-500/30">
                {approvedTenants.length} active
              </span>
            )}
          </div>
        </div>
        {approvedTenants.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-main-light/30 rounded-full mb-4">
              <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <p className="text-gray-400 text-lg font-medium">No approved tenants</p>
            <p className="text-gray-500 text-sm mt-2">Approved tenants will appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {approvedTenants.map((user) => (
              <div
                key={user.id}
                className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.01] border border-white/5"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      {user.tenant && (
                        <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-green-600 to-green-700 rounded-lg shadow-md">
                          <span className="text-white font-bold text-sm">
                            {user.tenant.companyName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        {user.tenant && (
                          <h3 className="text-lg font-bold text-white">{user.tenant.companyName}</h3>
                        )}
                        <p className="text-gray-400 text-sm">{user.email}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ml-13">
                      {user.tenant && (
                        <div className="flex items-center gap-2 text-gray-300 text-sm">
                          <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {user.tenant.phoneNumber}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-300 text-sm">
                        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Approved: {user.approvedAt ? new Date(user.approvedAt).toLocaleDateString() : 'N/A'}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-semibold rounded-full border border-green-500/30">
                          Active
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-6">
                    <button
                      onClick={() => handleViewDetails(user)}
                      className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 hover:scale-105 active:scale-95"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View Details
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      disabled={actionLoading === `delete-${user.id}`}
                      className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed flex items-center gap-2 hover:scale-105 active:scale-95"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      {actionLoading === `delete-${user.id}` ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedUser && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowDetailModal(false);
            setSelectedUser(null);
          }}
        >
          <div
            className="bg-gradient-to-br from-main-medium via-main-medium to-main-dark rounded-2xl p-8 max-w-2xl w-full shadow-2xl border border-white/5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                {selectedUser.tenant && (
                  <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl shadow-lg">
                    <span className="text-white font-bold text-lg">
                      {selectedUser.tenant.companyName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <h3 className="text-2xl font-bold text-white tracking-tight">Tenant Details</h3>
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedUser(null);
                }}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-main-light/30 rounded-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              <div className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-4">
                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">Email</label>
                <p className="text-white text-lg font-medium">{selectedUser.email}</p>
              </div>
              {selectedUser.tenant && (
                <>
                  <div className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-4">
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">Company Name</label>
                    <p className="text-white text-lg font-medium">{selectedUser.tenant.companyName}</p>
                  </div>
                  <div className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-4">
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">Phone Number</label>
                    <p className="text-white text-lg font-medium">{selectedUser.tenant.phoneNumber}</p>
                  </div>
                </>
              )}
              <div className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-4">
                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">Status</label>
                <p className="mt-1">
                  <span className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${selectedUser.approved ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'}`}>
                    {selectedUser.approved ? '✓ Approved' : '⏳ Pending'}
                  </span>
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-4">
                  <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">Account Created</label>
                  <p className="text-white text-sm font-medium">{new Date(selectedUser.createdAt).toLocaleString()}</p>
                </div>
                <div className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-4">
                  <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">Approved At</label>
                  <p className="text-white text-sm font-medium">
                    {selectedUser.approvedAt ? new Date(selectedUser.approvedAt).toLocaleString() : 'Not approved yet'}
                  </p>
                </div>
              </div>
              <div className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-4">
                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">Last Updated</label>
                <p className="text-white text-sm font-medium">{new Date(selectedUser.updatedAt).toLocaleString()}</p>
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 bg-main-light/50 hover:bg-main-light/70 text-white font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Close
              </button>
              <button
                onClick={() => handleDelete(selectedUser.id)}
                disabled={actionLoading === `delete-${selectedUser.id}`}
                className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {actionLoading === `delete-${selectedUser.id}` ? 'Deleting...' : 'Delete Tenant'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantManagement;
